import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Globe, 
  CheckCircle, 
  FileText, 
  Users, 
  Building2, 
  Clock, 
  Shield,
  ArrowRight,
  Briefcase,
  GraduationCap,
  Heart,
  Plane,
  Award,
  FileCheck
} from 'lucide-react';
import { Button } from './ui/button';
import { Navbar } from './Navbar';
import { Footer } from './Footer';

const visaTypes = [
  {
    icon: Briefcase,
    title: 'H-1B Specialty Occupation',
    description: 'For professionals in specialty occupations requiring at least a bachelor\'s degree.',
    features: ['Initial 3-year validity', 'Extendable up to 6 years', 'Dual intent allowed']
  },
  {
    icon: Building2,
    title: 'L-1 Intracompany Transfer',
    description: 'For managers, executives, and specialized knowledge employees transferring within a company.',
    features: ['L-1A for managers/executives', 'L-1B for specialized knowledge', 'Blanket petitions available']
  },
  {
    icon: Award,
    title: 'O-1 Extraordinary Ability',
    description: 'For individuals with extraordinary ability in sciences, arts, education, business, or athletics.',
    features: ['No annual cap', 'Initial 3-year stay', 'Unlimited extensions']
  },
  {
    icon: Plane,
    title: 'E-2 Treaty Investor',
    description: 'For investors from treaty countries making substantial investments in U.S. businesses.',
    features: ['No minimum investment amount', 'Renewable indefinitely', 'Spouse can work']
  },
  {
    icon: GraduationCap,
    title: 'F-1 Student Visa',
    description: 'For international students pursuing academic studies at U.S. institutions.',
    features: ['OPT work authorization', 'STEM OPT extension', 'CPT available']
  },
  {
    icon: Heart,
    title: 'Family-Based Immigration',
    description: 'Petitions for immediate relatives and family preference categories.',
    features: ['Spouse and children', 'Parents of U.S. citizens', 'Siblings of U.S. citizens']
  }
];

const services = [
  {
    title: 'Visa Applications & Petitions',
    items: [
      'H-1B specialty occupation visas',
      'L-1 intracompany transferee visas',
      'O-1 extraordinary ability visas',
      'E-1/E-2 treaty trader/investor visas',
      'TN NAFTA professional visas',
      'R-1 religious worker visas'
    ]
  },
  {
    title: 'Green Card Processing',
    items: [
      'Employment-based green cards (EB-1, EB-2, EB-3)',
      'PERM labor certification',
      'National Interest Waiver (NIW)',
      'Family-based green cards',
      'Adjustment of status applications',
      'Consular processing support'
    ]
  },
  {
    title: 'Corporate Immigration',
    items: [
      'I-9 compliance audits',
      'E-Verify enrollment and management',
      'Immigration policy development',
      'Workforce immigration planning',
      'M&A immigration due diligence',
      'Global mobility support'
    ]
  },
  {
    title: 'Citizenship & Naturalization',
    items: [
      'N-400 naturalization applications',
      'Citizenship test preparation',
      'Derivative citizenship claims',
      'Certificate of citizenship',
      'Passport applications',
      'Dual citizenship guidance'
    ]
  }
];

const process = [
  {
    step: '01',
    title: 'Initial Consultation',
    description: 'We assess your immigration goals, review your background, and identify the best visa pathway for your situation.'
  },
  {
    step: '02',
    title: 'Document Collection',
    description: 'We provide a comprehensive checklist and assist in gathering all required documents, ensuring nothing is missed.'
  },
  {
    step: '03',
    title: 'Application Preparation',
    description: 'Our team meticulously prepares your petition, forms, and supporting documents with attention to every detail.'
  },
  {
    step: '04',
    title: 'Filing & Tracking',
    description: 'We file your application and provide regular status updates, responding promptly to any USCIS requests.'
  }
];

export const ImmigrationPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#fdfdfc]">
      <Navbar />
      
      {/* Hero Section */}
      <section 
        className="pt-32 pb-20 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #fdfdfc 0%, rgba(10,25,47,0.08) 50%, rgba(217,48,37,0.1) 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-96 h-96 bg-[#0a192f]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-[#D93025]/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Back Button */}
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-[#64748b] hover:text-[#0a192f] transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Home</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a192f] rounded-full mb-6">
              <Globe className="w-4 h-4 text-white" />
              <span className="text-xs font-bold uppercase tracking-widest text-white">
                Immigration Services
              </span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0a192f] leading-[1.1] mb-6">
              Comprehensive Immigration Support for Your Journey
            </h1>
            
            <p className="text-lg text-[#64748b] leading-relaxed mb-8 max-w-2xl">
              Navigate the complexities of U.S. immigration with confidence. Our experienced team 
              provides end-to-end support for visa applications, green cards, work permits, and 
              corporate immigration compliance.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => document.querySelector('#contact-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#0a192f] text-white rounded-xl px-8 py-4 font-medium hover:bg-[#0a192f]/90 transition-all hover:scale-[1.02] shadow-lg shadow-[#0a192f]/20"
                data-testid="immigration-cta"
              >
                Start Your Application
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={() => document.querySelector('#visa-types')?.scrollIntoView({ behavior: 'smooth' })}
                variant="outline"
                className="bg-white text-[#0a192f] rounded-xl px-8 py-4 font-medium hover:bg-[#f5f5f0] transition-all border border-[#e2e2dc]"
              >
                Explore Visa Types
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0a192f]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '5,000+', label: 'Visas Processed' },
              { value: '98%', label: 'Approval Rate' },
              { value: '15+', label: 'Years Experience' },
              { value: '500+', label: 'Corporate Clients' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="text-center"
              >
                <p className="font-heading text-3xl md:text-4xl font-semibold text-white mb-2">
                  {stat.value}
                </p>
                <p className="text-sm text-white/60">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visa Types Section */}
      <section id="visa-types" className="py-24 bg-[#f5f5f0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-[#D93025]">
              Visa Categories
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-[#0a192f] mt-4 mb-6">
              Visa Types We Support
            </h2>
            <p className="text-base md:text-lg text-[#64748b] max-w-2xl mx-auto">
              From work visas to family-based immigration, we provide expert support across 
              all major visa categories.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visaTypes.map((visa, index) => (
              <motion.div
                key={visa.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white rounded-xl border border-[#e2e2dc] p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-[#0a192f] rounded-xl flex items-center justify-center mb-4">
                  <visa.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-heading text-lg font-medium text-[#0a192f] mb-2">
                  {visa.title}
                </h3>
                <p className="text-sm text-[#64748b] mb-4">
                  {visa.description}
                </p>
                <ul className="space-y-2">
                  {visa.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#64748b]">
                      <CheckCircle className="w-4 h-4 text-[#D93025] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-[#fdfdfc]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-[#D93025]">
              What We Offer
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-[#0a192f] mt-4 mb-6">
              Our Immigration Services
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white rounded-xl border border-[#e2e2dc] p-8 shadow-sm"
              >
                <h3 className="font-heading text-xl font-medium text-[#0a192f] mb-6 pb-4 border-b border-[#e2e2dc]">
                  {service.title}
                </h3>
                <ul className="grid grid-cols-1 gap-3">
                  {service.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#D93025] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#64748b]">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section 
        className="py-24 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(10,25,47,0.06) 0%, #f5f5f0 50%, rgba(217,48,37,0.08) 100%)'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-[#D93025]">
              Our Process
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-[#0a192f] mt-4 mb-6">
              How We Work With You
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className="relative"
              >
                <div className="bg-white rounded-xl border border-[#e2e2dc] p-6 shadow-sm h-full">
                  <span className="inline-block bg-[#0a192f] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                    Step {step.step}
                  </span>
                  <h3 className="font-heading text-lg font-medium text-[#0a192f] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm text-[#64748b]">
                    {step.description}
                  </p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-[#0a192f]/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#0a192f]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-[#D93025]">
              Why Choose Us
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-white mt-4 mb-6">
              The Redline Advantage
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Expert Knowledge',
                description: 'Our team stays current with the latest immigration laws, policies, and USCIS procedures.'
              },
              {
                icon: Clock,
                title: 'Timely Processing',
                description: 'We understand the importance of deadlines and ensure your applications are filed promptly.'
              },
              {
                icon: Users,
                title: 'Personalized Service',
                description: 'Every case is unique. We provide tailored solutions based on your specific circumstances.'
              },
              {
                icon: FileCheck,
                title: 'Thorough Documentation',
                description: 'Meticulous attention to detail ensures complete and accurate application packages.'
              },
              {
                icon: Globe,
                title: 'Global Perspective',
                description: 'Experience with clients from over 50 countries gives us unique insights into immigration challenges.'
              },
              {
                icon: Building2,
                title: 'Corporate Expertise',
                description: 'Specialized support for businesses managing international workforce immigration needs.'
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6"
              >
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-heading text-lg font-medium text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-white/60">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact-section" className="py-24 bg-[#f5f5f0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-[#0a192f] mb-6">
              Ready to Start Your Immigration Journey?
            </h2>
            <p className="text-lg text-[#64748b] mb-8">
              Contact us today for a confidential consultation. Our team is ready to help you 
              navigate the path to your immigration goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#contact">
                <Button
                  className="bg-[#0a192f] text-white rounded-xl px-8 py-4 font-medium hover:bg-[#0a192f]/90 transition-all hover:scale-[1.02] shadow-lg shadow-[#0a192f]/20"
                >
                  Schedule Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/">
                <Button
                  variant="outline"
                  className="bg-white text-[#0a192f] rounded-xl px-8 py-4 font-medium hover:bg-[#f5f5f0] transition-all border border-[#e2e2dc]"
                >
                  View All Services
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
